import { Receipt, CheckCircle2, FileSpreadsheet, FileUp, Bell, UserCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { formatPrice, formatDate } from '../../lib/format';
import { StatCard, Spinner, EmptyState, StatusBadge } from '../../components/ui';
import QuickLinks from '../../components/QuickLinks';
import DashboardStatus from '../../components/DashboardStatus';
import { useLivePoll } from '../../lib/useLivePoll';

const QUICK_LINKS = [
  { to: '/accounts/expenses', label: 'Expense Review', icon: <Receipt className="h-5 w-5" />, description: 'Approve claims' },
  { to: '/accounts/monthly-expenses', label: 'Monthly Expenses', icon: <FileSpreadsheet className="h-5 w-5" />, description: 'Site uploads' },
  { to: '/accounts/expense-reports', label: 'Expense Reports', icon: <FileUp className="h-5 w-5" />, description: 'Reports' },
  { to: '/accounts/account-reports', label: 'Account Reports', icon: <FileSpreadsheet className="h-5 w-5" />, description: 'Financial summaries' },
  { to: '/accounts/notices', label: 'Notices', icon: <Bell className="h-5 w-5" />, description: 'Announcements' },
  { to: '/accounts/profile', label: 'My Profile', icon: <UserCircle className="h-5 w-5" />, description: 'Account details' },
];

interface AccountDashboardData {
  stats: {
    totalExpenses: number;
    pendingExpenses: number;
    approvedExpenses: number;
    pendingNgn: number;
    pendingUsd: number;
    expenseReports: number;
  };
  recentExpenses: Array<{
    id: number; category: string; amount: number; currency: string;
    status: string; expense_date: string; user_name: string;
  }>;
  upcomingMeetings: Array<{ id: number; title: string; meeting_date: string; duration: number; meeting_url: string; status: string }>;
}

export default function Dashboard() {
  const { data, loading, refreshing, lastUpdated, reload } = useLivePoll<AccountDashboardData>(
    () => api.get<AccountDashboardData>('/dashboard')
  );

  if (loading) return <Spinner label="Loading dashboard..." />;
  if (!data) return <EmptyState message="Could not load dashboard data." />;

  const s = data.stats;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <DashboardStatus lastUpdated={lastUpdated} refreshing={refreshing} onRefresh={reload} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={<Receipt className="h-8 w-8" />}
          label="Total Expenses"
          value={s.totalExpenses}
          sub={`${s.approvedExpenses} approved`}
        />
        <StatCard
          icon={<Receipt className="h-8 w-8" />}
          label="Pending Approval"
          value={s.pendingExpenses}
          color="text-yellow-600"
        />
        <StatCard
          icon={<CheckCircle2 className="h-8 w-8" />}
          label="Pending Total (NGN)"
          value={formatPrice(s.pendingNgn, 'NGN')}
          color="text-green-600"
        />
        <StatCard
          icon={<FileSpreadsheet className="h-8 w-8" />}
          label="Expense Reports"
          value={s.expenseReports}
          color="text-purple-600"
          sub={`Pending USD: ${formatPrice(s.pendingUsd, 'USD')}`}
        />
      </div>

      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-xl font-semibold mb-4">Recent Expenses</h2>
        {data.recentExpenses.length === 0 ? (
          <EmptyState message="No expenses yet." />
        ) : (
          <div className="space-y-4">
            {data.recentExpenses.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0">
                <div>
                  <p className="font-medium">{e.category}</p>
                  <p className="text-sm text-gray-500">{e.user_name} · {formatDate(e.expense_date)}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="font-medium">{formatPrice(e.amount, e.currency)}</span>
                  <StatusBadge status={e.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8">
        <QuickLinks links={QUICK_LINKS} title="All Information" />
      </div>
    </div>
  );
}